import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, getDocs, doc, getDoc } from 'firebase/firestore'; 

// Modals
import { PlayerDetailsModal } from './modals/PlayerDetailsModal';
import { VehicleDetailsModal } from './modals/VehicleDetailsModal';
import { StashDetailsModal } from './modals/StashDetailsModal';

// Chart.js imports (assuming Chart.js is loaded via CDN in App.js)
// These registrations ensure Chart.js components are available
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Helper function to format numbers
const formatNumber = (num) => {
  if (num === null || num === undefined) return 'N/A';
  return num.toLocaleString();
};

// Helper function to format money (from Economy.js context)
const formatMoney = (value) => {
  if (value === null || value === undefined) return 'N/A';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value}`;
};

// StatCard Component - Enhanced with hot interactive design
const StatCard = ({ title, value, icon, iconColorClass, index }) => (
  <div 
    className="group relative overflow-hidden glass-card rounded-xl p-4 hover:bg-white/10 transition-all duration-700 hover:scale-105 hover:-translate-y-2 hover:shadow-2xl border border-white/10 hover:border-blue-400/50 animate-fadeIn hover-lift"
    style={{ animationDelay: `${(index || 0) * 0.1}s` }}
  >
    {/* Shimmer overlay */}
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
    
    {/* Gradient background overlay on hover */}
    <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-600/5 to-cyan-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
    
    {/* Animated border glow */}
    <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-blue-400/20 via-transparent to-purple-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm"></div>
    
    <div className="relative flex items-center">
      <div className="relative mr-4 flex-shrink-0">
        <div className={`w-12 h-12 rounded-xl ${iconColorClass.replace('text-', 'bg-').replace('400', '500/20')} flex items-center justify-center group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 shadow-lg`}>
          {React.cloneElement(icon, { className: `${icon.props.className} ${iconColorClass} text-lg` })}
        </div>
        {/* Animated ring */}
        <div className={`absolute -inset-1 rounded-xl ${iconColorClass.replace('text-', 'bg-').replace('400', '500/10')} opacity-0 group-hover:opacity-100 animate-ping transition-opacity duration-300`}></div>
      </div>
      <div className="flex-grow min-w-0">
        <p className="text-xs font-bold text-gray-300 mb-1 uppercase tracking-wide group-hover:text-gray-200 transition-colors duration-300 truncate">{title}</p>
        <p className="text-xl font-black text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-purple-400 group-hover:bg-clip-text transition-all duration-500 transform group-hover:scale-105 truncate">
          {value}
        </p>
        {/* Progress bar effect */}
        <div className="h-0.5 w-0 bg-gradient-to-r from-blue-400 to-purple-400 group-hover:w-full transition-all duration-700 rounded-full mt-2"></div>
      </div>
    </div>
    
    {/* Floating particles */}
    <div className="absolute top-1 right-1 w-1 h-1 rounded-full bg-blue-400/60 opacity-0 group-hover:opacity-100 animate-bounce transition-opacity duration-300" style={{ animationDelay: '0.2s' }}></div>
    <div className="absolute bottom-1 left-1 w-1 h-1 rounded-full bg-purple-400/60 opacity-0 group-hover:opacity-100 animate-bounce transition-opacity duration-300" style={{ animationDelay: '0.8s' }}></div>
  </div>
);


// Main Overview Component
export function Overview({ db, setModalMessage }) { // Receive db and setModalMessage as props
  const [overviewData, setOverviewData] = useState({
    totalPlayers: 0,
    totalVehicles: 0,
    totalFactions: 0,
    totalStashes: 0,
    players: [],
    factions: [],
    vehicles: [],
    stashes: []
  });

  const [category, setCategory] = useState('players');
  
  // Debug: Log category changes
  useEffect(() => {
    console.log('Category changed to:', category);
  }, [category]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  const [isVehicleModalOpen, setIsVehicleModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const [isStashModalOpen, setIsStashModalOpen] = useState(false);
  const [selectedStash, setSelectedStash] = useState(null);

  // Fetch data function
  const fetchOverviewData = useCallback(async () => {
    if (!db) {
        setError("Firestore database not available.");
        setLoading(false);
        return;
    }
    try {
      setLoading(true);
      setError(null);

      // Fetch overview counts from overview/{category} documents
      const playersDocRef = doc(db, 'overview', 'players');
      const playersDocSnap = await getDoc(playersDocRef);
      const totalPlayersCount = playersDocSnap.exists() ? playersDocSnap.data().total_players || 0 : 0;

      const vehiclesDocRef = doc(db, 'overview', 'vehicles');
      const vehiclesDocSnap = await getDoc(vehiclesDocRef);
      const totalVehiclesCount = vehiclesDocSnap.exists() ? vehiclesDocSnap.data().total_vehicles || 0 : 0;

      const factionsDocRef = doc(db, 'overview', 'factions');
      const factionsDocSnap = await getDoc(factionsDocRef);
      const totalFactionsCount = factionsDocSnap.exists() ? factionsDocSnap.data().total_factions || 0 : 0;
      const factionsDetailsArray = factionsDocSnap.exists() ? factionsDocSnap.data().factions || [] : []; // Assuming factions details are directly in the document

      const stashesDocRef = doc(db, 'overview', 'stashes');
      const stashesDocSnap = await getDoc(stashesDocRef);
      const totalStashesCount = stashesDocSnap.exists() ? stashesDocSnap.data().total_stashes || 0 : 0;

      // Helper to fetch details from sub-collections
      const fetchCollectionDetails = async (collectionPath) => {
        const q = query(collection(db, collectionPath));
        const querySnapshot = await getDocs(q);
        const data = [];
        querySnapshot.forEach((doc) => {
          data.push(doc.data());
        });
        return data;
      };

      // Fetch detailed lists from sub-collections
      const playersDetails = await fetchCollectionDetails('overview/players/player_details');
      const vehiclesDetails = await fetchCollectionDetails('overview/vehicles/vehicle_details');
      const stashesDetails = await fetchCollectionDetails('overview/stashes/stash_details');


      setOverviewData({
        totalPlayers: totalPlayersCount,
        totalVehicles: totalVehiclesCount,
        totalFactions: totalFactionsCount,
        totalStashes: totalStashesCount,
        players: playersDetails,
        factions: factionsDetailsArray,
        vehicles: vehiclesDetails,
        stashes: stashesDetails
      });

    } catch (err) {
      console.error("Error fetching overview data:", err);
      setError("Failed to load overview data. " + err.message);
      setModalMessage("Failed to load overview data: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [db, setModalMessage]); // db and setModalMessage are dependencies

  useEffect(() => {
    fetchOverviewData();
    const interval = setInterval(fetchOverviewData, 60000); // Refresh every 60 seconds
    return () => clearInterval(interval);
  }, [fetchOverviewData]); // fetchOverviewData is a dependency because it's wrapped in useCallback

  const handleOpenModal = useCallback((type, data) => {
    if (type === 'player') {
      setSelectedPlayer(data);
      setIsPlayerModalOpen(true);
    }
    if (type === 'vehicle') {
      setSelectedVehicle(data);
      setIsVehicleModalOpen(true);
    }
    if (type === 'stash') {
      setSelectedStash(data);
      setIsStashModalOpen(true);
    }
  }, []);

  const handleCloseModal = useCallback((type) => {
    if (type === 'player') {
      setIsPlayerModalOpen(false);
      setSelectedPlayer(null);
    }
    if (type === 'vehicle') {
      setIsVehicleModalOpen(false);
      setSelectedVehicle(null);
    }
    if (type === 'stash') {
      setIsStashModalOpen(false);
      setSelectedStash(null);
    }
  }, []);

  if (loading) {
    return (
      <div className="glass-card rounded-2xl h-full flex items-center justify-center p-12 animate-fadeIn">
        <div className="text-center">
          <div className="relative mb-8">
            <div className="animate-spin rounded-full h-20 w-20 border-4 border-blue-500/20 border-t-blue-500 mx-auto animate-glow"></div>
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500/20 to-purple-500/20 animate-pulse"></div>
          </div>
          <div className="space-y-3">
            <h3 className="text-3xl font-bold gradient-text animate-slideInLeft">Loading Server Overview</h3>
            <p className="text-gray-300 text-lg animate-fadeIn" style={{ animationDelay: '0.3s' }}>Gathering real-time statistics...</p>
            <div className="flex items-center justify-center space-x-2 animate-slideInLeft" style={{ animationDelay: '0.5s' }}>
              <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce"></div>
              <div className="w-2 h-2 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 rounded-full bg-cyan-400 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-card rounded-2xl h-full flex items-center justify-center p-12 animate-fadeIn">
        <div className="text-center">
          <div className="relative mb-8">
            <div className="w-24 h-24 bg-gradient-to-br from-red-500/20 to-orange-500/20 rounded-2xl flex items-center justify-center mx-auto shadow-lg animate-bounce">
              <i className="fas fa-exclamation-triangle text-4xl text-red-400 animate-pulse"></i>
            </div>
            <div className="absolute -inset-2 bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded-2xl animate-ping"></div>
          </div>
          <div className="space-y-4">
            <h3 className="text-3xl font-bold gradient-text animate-slideInLeft">Connection Error</h3>
            <p className="text-gray-300 text-lg mb-6 animate-fadeIn" style={{ animationDelay: '0.2s' }}>{error}</p>
            <div className="space-y-2 animate-slideInLeft" style={{ animationDelay: '0.4s' }}>
              <p className="text-sm text-gray-400">Please check your connection and try again</p>
              <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
                <i className="fas fa-database"></i>
                <span>Firestore connection and security rules</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card rounded-2xl h-full flex flex-col p-6 animate-fadeIn overflow-hidden">
      {/* Enhanced Header Section */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 animate-slideInLeft flex-shrink-0">
        <div className="mb-4 lg:mb-0 relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl blur-xl animate-pulse"></div>
          <div className="relative">
            <h2 className="text-3xl font-black gradient-text flex items-center mb-3 animate-slideInLeft">
              <div className="relative mr-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center shadow-lg animate-float">
                  <i className="fas fa-chart-pie text-2xl text-blue-400 animate-pulse"></i>
                </div>
                <div className="absolute -inset-1 rounded-xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 animate-ping opacity-75"></div>
              </div>
              <div>
                Server Overview
                <div className="h-1 w-0 bg-gradient-to-r from-blue-400 to-purple-400 animate-[width-expand_2s_ease-out_forwards] rounded-full"></div>
              </div>
            </h2>
            <div className="flex items-center space-x-4 animate-fadeIn" style={{ animationDelay: '0.3s' }}>
              <div className="flex items-center space-x-2 text-gray-300">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                <span className="text-sm font-medium">Real-time monitoring active</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-500 text-xs">
                <i className="fas fa-sync-alt animate-spin" style={{ animationDuration: '3s' }}></i>
                <span>Auto-refresh enabled</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Enhanced Category Selector */}
        <div className="relative group animate-bounceIn" style={{ animationDelay: '0.5s' }}>
          <div className="glass-card rounded-xl p-2 border border-white/20 group-hover:border-blue-400/50 transition-all duration-500 shadow-lg hover:shadow-2xl hover:shadow-blue-500/25">
            <div className="flex gap-2">
              {['players', 'vehicles', 'factions', 'stashes'].map((cat, index) => (
                <button 
                  key={cat}
                  className={`relative overflow-hidden px-4 py-2 rounded-lg text-xs font-bold transition-all duration-500 transform hover:scale-105 cursor-pointer z-10 ${
                    category === cat 
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg animate-glow' 
                      : 'text-gray-400 hover:text-white hover:bg-white/10 hover:shadow-lg'
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Category button clicked:', cat);
                    console.log('Current category before change:', category);
                    setCategory(cat);
                    console.log('setCategory called with:', cat);
                  }}
                  style={{ 
                    animationDelay: `${index * 0.1}s`,
                    pointerEvents: 'auto'
                  }}
                >
                  {category === cat && (
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 animate-pulse pointer-events-none"></div>
                  )}
                  <div className="relative flex items-center space-x-1.5 pointer-events-none">
                    <i className={`fas text-xs ${
                      cat === 'players' ? 'fa-users' : 
                      cat === 'vehicles' ? 'fa-car-side' : 
                      cat === 'factions' ? 'fa-shield-alt' : 'fa-box-open'
                    }`}></i>
                    <span className="text-xs">{cat.charAt(0).toUpperCase() + cat.slice(1)}</span>
                  </div>
                  {category === cat && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-shimmer pointer-events-none"></div>
                  )}
                </button>
              ))}
            </div>
          </div>
          {/* Glow effect */}
          <div className="absolute -inset-1 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-xl opacity-0 group-hover:opacity-100 blur-xl transition-all duration-500 pointer-events-none"></div>
        </div>
      </div>

      {/* Enhanced Quick Stats Container */}
      <div className="relative overflow-hidden glass-card rounded-xl p-6 mb-6 border border-white/20 hover:border-blue-400/50 transition-all duration-500 animate-slideInLeft" style={{ animationDelay: '0.2s' }}>
        {/* Background animation */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 via-purple-600/3 to-cyan-600/5 opacity-0 hover:opacity-100 transition-opacity duration-500"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/2 to-transparent -translate-x-full animate-shimmer"></div>
        
        <div className="relative">
          <h3 className="text-xl font-black text-white mb-6 flex items-center animate-slideInLeft">
            <div className="relative mr-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center animate-heartbeat">
                <i className="fas fa-chart-line text-purple-400 text-lg"></i>
              </div>
              <div className="absolute -inset-1 rounded-lg bg-gradient-to-br from-purple-500/10 to-pink-500/10 animate-ping opacity-50"></div>
            </div>
            <div>
              Global Statistics
              <div className="h-0.5 w-0 bg-gradient-to-r from-purple-400 to-pink-400 animate-[width-expand_1.5s_ease-out_0.5s_forwards] rounded-full"></div>
            </div>
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              title="Total Players" 
              value={formatNumber(overviewData.totalPlayers)} 
              icon={<i className="fas fa-users"></i>} 
              iconColorClass="text-purple-400"
              index={0}
            />
            <StatCard 
              title="Total Vehicles" 
              value={formatNumber(overviewData.totalVehicles)} 
              icon={<i className="fas fa-car-side"></i>} 
              iconColorClass="text-blue-400"
              index={1}
            />
            <StatCard 
              title="Total Factions" 
              value={formatNumber(overviewData.totalFactions)} 
              icon={<i className="fas fa-shield-alt"></i>} 
              iconColorClass="text-yellow-400"
              index={2}
            />
            <StatCard 
              title="Total Stashes" 
              value={formatNumber(overviewData.totalStashes)} 
              icon={<i className="fas fa-box-open"></i>} 
              iconColorClass="text-green-400"
              index={3}
            />
          </div>
        </div>
      </div>

    {/* Enhanced Players Section */}
    {category === 'players' && (
      <div className="flex-1 relative overflow-hidden glass-card rounded-xl p-6 border border-white/20 hover:border-purple-400/50 transition-all duration-500 flex flex-col animate-fadeIn min-h-0">
        {/* Background animation */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-600/5 via-blue-600/3 to-purple-600/5 opacity-0 hover:opacity-100 transition-opacity duration-500"></div>
        
        <div className="relative flex-shrink-0">
          <h3 className="text-xl font-black text-white mb-6 flex items-center animate-slideInLeft">
            <div className="relative mr-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center shadow-lg animate-heartbeat">
                <i className="fas fa-users text-purple-400 text-lg"></i>
              </div>
              <div className="absolute -inset-1 rounded-lg bg-gradient-to-br from-purple-500/10 to-blue-500/10 animate-ping opacity-50"></div>
            </div>
            <div>
              All Players ({overviewData.players.length})
              <div className="h-0.5 w-0 bg-gradient-to-r from-purple-400 to-blue-400 animate-[width-expand_1.5s_ease-out_0.3s_forwards] rounded-full"></div>
            </div>
          </h3>
        </div>
        
        <div className="flex-1 overflow-y-auto custom-scrollbar relative min-h-0">
          {overviewData.players.length === 0 ? (
            <div className="glass-card rounded-xl p-8 text-center animate-fadeIn">
              <div className="relative mb-6">
                {/* Animated background rings */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-500/10 to-blue-500/10 animate-ping" style={{ animationDuration: '3s' }}></div>
                <div className="absolute inset-4 rounded-full bg-gradient-to-r from-purple-500/5 to-blue-500/5 animate-ping" style={{ animationDuration: '2s', animationDelay: '0.5s' }}></div>
                
                <div className="relative w-20 h-20 mx-auto rounded-xl bg-gradient-to-br from-purple-600/20 to-blue-600/20 flex items-center justify-center animate-float">
                  <i className="fas fa-users text-4xl text-purple-400 animate-pulse"></i>
                </div>
              </div>
              <h4 className="text-xl font-bold gradient-text mb-3 animate-slideInLeft">No Players Found</h4>
              <p className="text-gray-300 animate-fadeIn" style={{ animationDelay: '0.2s' }}>No player data is currently available</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {overviewData.players.map((player, index) => (
                <div 
                  key={index} 
                  className="group relative overflow-hidden glass-card rounded-xl p-4 hover:bg-white/10 transition-all duration-700 hover:scale-105 hover:-translate-y-1 border border-white/10 hover:border-purple-400/50 animate-fadeIn hover:shadow-xl hover:shadow-purple-500/25"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  {/* Enhanced hover effects */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-600/10 via-blue-600/5 to-purple-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center flex-grow min-w-0">
                        <div className="relative mr-3 flex-shrink-0">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                            <i className="fas fa-user text-purple-400"></i>
                          </div>
                          <div className="absolute -inset-1 bg-gradient-to-br from-purple-500/10 to-blue-500/10 rounded-lg opacity-0 group-hover:opacity-100 animate-ping transition-opacity duration-300"></div>
                        </div>
                        <div className="min-w-0 flex-grow">
                          <h4 className="text-white font-bold group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-purple-400 group-hover:to-blue-400 group-hover:bg-clip-text transition-all duration-500 mb-1 truncate">
                            {player.name || 'Unknown Player'}
                          </h4>
                          <p className="text-xs text-gray-400 font-mono bg-gray-800/50 px-2 py-1 rounded-md truncate">{player.identifier}</p>
                        </div>
                      </div>
                      <button 
                        className="relative overflow-hidden bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-bold px-3 py-1.5 rounded-lg text-xs opacity-0 group-hover:opacity-100 transition-all duration-500 hover:scale-110 hover:-translate-y-1 shadow-lg flex-shrink-0 ml-2"
                        onClick={() => handleOpenModal('player', player)}
                      >
                        <div className="relative flex items-center space-x-1">
                          <i className="fas fa-info-circle"></i>
                          <span>Details</span>
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-400/20 to-blue-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                      </button>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center p-2 bg-white/5 rounded-lg group-hover:bg-white/10 transition-all duration-300">
                        <span className="text-gray-400 font-medium flex items-center">
                          <i className="fab fa-discord mr-2 text-purple-400 text-xs"></i>
                          Discord:
                        </span>
                        <span className="text-white font-semibold text-xs truncate max-w-[100px]">{player.discord || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between items-center p-2 bg-white/5 rounded-lg group-hover:bg-white/10 transition-all duration-300">
                        <span className="text-gray-400 font-medium flex items-center">
                          <i className="fas fa-users mr-2 text-blue-400 text-xs"></i>
                          Group:
                        </span>
                        <span className="text-white font-semibold text-xs truncate max-w-[100px]">{player.group || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )}
    {/* Enhanced Vehicles Section */}
    {category === 'vehicles' && (
      <div className="flex-1 relative overflow-hidden glass-card rounded-xl p-6 border border-white/20 hover:border-green-400/50 transition-all duration-500 flex flex-col animate-fadeIn min-h-0">
        {/* Background animation */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-600/5 via-cyan-600/3 to-green-600/5 opacity-0 hover:opacity-100 transition-opacity duration-500"></div>
        
        <div className="relative flex-shrink-0">
          <h3 className="text-xl font-black text-white mb-6 flex items-center animate-slideInLeft">
            <div className="relative mr-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500/20 to-cyan-500/20 flex items-center justify-center shadow-lg animate-heartbeat">
                <i className="fas fa-car-side text-green-400 text-lg"></i>
              </div>
              <div className="absolute -inset-1 rounded-lg bg-gradient-to-br from-green-500/10 to-cyan-500/10 animate-ping opacity-50"></div>
            </div>
            <div>
              All Vehicles ({overviewData.vehicles.length})
              <div className="h-0.5 w-0 bg-gradient-to-r from-green-400 to-cyan-400 animate-[width-expand_1.5s_ease-out_0.3s_forwards] rounded-full"></div>
            </div>
          </h3>
        </div>
        
        <div className="flex-1 overflow-y-auto custom-scrollbar relative min-h-0">
          {overviewData.vehicles.length === 0 ? (
            <div className="glass-card rounded-xl p-8 text-center animate-fadeIn">
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-green-500/10 to-cyan-500/10 animate-ping" style={{ animationDuration: '3s' }}></div>
                <div className="absolute inset-4 rounded-full bg-gradient-to-r from-green-500/5 to-cyan-500/5 animate-ping" style={{ animationDuration: '2s', animationDelay: '0.5s' }}></div>
                
                <div className="relative w-20 h-20 mx-auto rounded-xl bg-gradient-to-br from-green-600/20 to-cyan-600/20 flex items-center justify-center animate-float">
                  <i className="fas fa-car-side text-4xl text-green-400 animate-pulse"></i>
                </div>
              </div>
              <h4 className="text-xl font-bold gradient-text mb-3 animate-slideInLeft">No Vehicles Found</h4>
              <p className="text-gray-300 animate-fadeIn" style={{ animationDelay: '0.2s' }}>No vehicle data is currently available</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {overviewData.vehicles.map((vehicle, index) => (
                <div 
                  key={index} 
                  className="group relative overflow-hidden glass-card rounded-xl p-4 hover:bg-white/10 transition-all duration-700 hover:scale-105 hover:-translate-y-1 border border-white/10 hover:border-green-400/50 animate-fadeIn hover:shadow-xl hover:shadow-green-500/25"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-green-600/10 via-cyan-600/5 to-green-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center flex-grow min-w-0">
                        <div className="relative mr-3 flex-shrink-0">
                          <div className="w-10 h-10 bg-gradient-to-br from-green-500/20 to-cyan-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                            <i className="fas fa-car text-green-400"></i>
                          </div>
                          <div className="absolute -inset-1 bg-gradient-to-br from-green-500/10 to-cyan-500/10 rounded-lg opacity-0 group-hover:opacity-100 animate-ping transition-opacity duration-300"></div>
                        </div>
                        <div className="min-w-0 flex-grow">
                          <h4 className="text-white font-bold group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-green-400 group-hover:to-cyan-400 group-hover:bg-clip-text transition-all duration-500 mb-1 truncate">
                            {vehicle.plate || 'Unknown Plate'}
                          </h4>
                          <p className="text-xs text-gray-400 bg-gray-800/50 px-2 py-1 rounded-md font-medium truncate">{vehicle.model_name || 'Unknown Model'}</p>
                        </div>
                      </div>
                      <button 
                        className="relative overflow-hidden bg-gradient-to-r from-green-600 to-cyan-600 hover:from-green-500 hover:to-cyan-500 text-white font-bold px-3 py-1.5 rounded-lg text-xs opacity-0 group-hover:opacity-100 transition-all duration-500 hover:scale-110 hover:-translate-y-1 shadow-lg flex-shrink-0 ml-2"
                        onClick={() => handleOpenModal('vehicle', vehicle)}
                      >
                        <div className="relative flex items-center space-x-1">
                          <i className="fas fa-info-circle"></i>
                          <span>Details</span>
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-r from-green-400/20 to-cyan-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                      </button>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center p-2 bg-white/5 rounded-lg group-hover:bg-white/10 transition-all duration-300">
                        <span className="text-gray-400 font-medium flex items-center">
                          <i className="fas fa-user mr-2 text-green-400 text-xs"></i>
                          Owner:
                        </span>
                        <span className="text-white font-mono text-xs truncate max-w-[120px]">{vehicle.owner_identifier || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )}

    {/* Enhanced Factions Section */}
    {category === 'factions' && (
      <div className="flex-1 relative overflow-hidden glass-card rounded-xl p-6 border border-white/20 hover:border-yellow-400/50 transition-all duration-500 flex flex-col animate-fadeIn min-h-0">
        {/* Background animation */}
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-600/5 via-orange-600/3 to-yellow-600/5 opacity-0 hover:opacity-100 transition-opacity duration-500"></div>
        
        <div className="relative flex-shrink-0">
          <h3 className="text-xl font-black text-white mb-6 flex items-center animate-slideInLeft">
            <div className="relative mr-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center shadow-lg animate-heartbeat">
                <i className="fas fa-shield-alt text-yellow-400 text-lg"></i>
              </div>
              <div className="absolute -inset-1 rounded-lg bg-gradient-to-br from-yellow-500/10 to-orange-500/10 animate-ping opacity-50"></div>
            </div>
            <div>
              All Factions ({overviewData.factions.length})
              <div className="h-0.5 w-0 bg-gradient-to-r from-yellow-400 to-orange-400 animate-[width-expand_1.5s_ease-out_0.3s_forwards] rounded-full"></div>
            </div>
          </h3>
        </div>
        
        <div className="flex-1 overflow-y-auto custom-scrollbar relative min-h-0">
          {overviewData.factions.length === 0 ? (
            <div className="glass-card rounded-xl p-8 text-center animate-fadeIn">
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-yellow-500/10 to-orange-500/10 animate-ping" style={{ animationDuration: '3s' }}></div>
                <div className="absolute inset-4 rounded-full bg-gradient-to-r from-yellow-500/5 to-orange-500/5 animate-ping" style={{ animationDuration: '2s', animationDelay: '0.5s' }}></div>
                
                <div className="relative w-20 h-20 mx-auto rounded-xl bg-gradient-to-br from-yellow-600/20 to-orange-600/20 flex items-center justify-center animate-float">
                  <i className="fas fa-shield-alt text-4xl text-yellow-400 animate-pulse"></i>
                </div>
              </div>
              <h4 className="text-xl font-bold gradient-text mb-3 animate-slideInLeft">No Factions Found</h4>
              <p className="text-gray-300 animate-fadeIn" style={{ animationDelay: '0.2s' }}>No faction data is currently available</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {overviewData.factions.map((faction, index) => (
                <div 
                  key={index} 
                  className="group relative overflow-hidden glass-card rounded-xl p-4 hover:bg-white/10 transition-all duration-700 hover:scale-105 hover:-translate-y-1 border border-white/10 hover:border-yellow-400/50 animate-fadeIn hover:shadow-xl hover:shadow-yellow-500/25"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-600/10 via-orange-600/5 to-yellow-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center flex-grow min-w-0">
                        <div className="relative mr-3 flex-shrink-0">
                          <div className="w-10 h-10 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                            <i className="fas fa-shield text-yellow-400"></i>
                          </div>
                          <div className="absolute -inset-1 bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-lg opacity-0 group-hover:opacity-100 animate-ping transition-opacity duration-300"></div>
                        </div>
                        <div className="min-w-0 flex-grow">
                          <h4 className="text-white font-bold group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-yellow-400 group-hover:to-orange-400 group-hover:bg-clip-text transition-all duration-500 mb-1 truncate">
                            {faction.name || 'Unknown Faction'}
                          </h4>
                          <p className="text-xs text-gray-400 bg-gray-800/50 px-2 py-1 rounded-md font-medium truncate">{faction.label || 'No Label'}</p>
                        </div>
                      </div>
                      <div className="flex-shrink-0 ml-2">
                        <span className={`px-2 py-1 rounded-lg text-xs font-bold shadow-lg transition-all duration-300 ${
                          faction.whitelisted 
                            ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white border border-green-500/30 hover:shadow-green-500/25' 
                            : 'bg-gradient-to-r from-gray-600 to-gray-700 text-gray-300 border border-gray-500/30 hover:shadow-gray-500/25'
                        }`}>
                          {faction.whitelisted ? 'WL' : 'Open'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center p-2 bg-white/5 rounded-lg group-hover:bg-white/10 transition-all duration-300">
                        <span className="text-gray-400 font-medium flex items-center">
                          <i className="fas fa-coins mr-2 text-yellow-400 text-xs"></i>
                          Balance:
                        </span>
                        <span className="text-white font-bold">{formatMoney(faction.society_balance)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )}

    {/* Enhanced Stashes Section */}
    {category === 'stashes' && (
      <div className="flex-1 relative overflow-hidden glass-card rounded-xl p-6 border border-white/20 hover:border-red-400/50 transition-all duration-500 flex flex-col animate-fadeIn min-h-0">
        {/* Background animation */}
        <div className="absolute inset-0 bg-gradient-to-br from-red-600/5 via-pink-600/3 to-red-600/5 opacity-0 hover:opacity-100 transition-opacity duration-500"></div>
        
        <div className="relative flex-shrink-0">
          <h3 className="text-xl font-black text-white mb-6 flex items-center animate-slideInLeft">
            <div className="relative mr-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-red-500/20 to-pink-500/20 flex items-center justify-center shadow-lg animate-heartbeat">
                <i className="fas fa-box-open text-red-400 text-lg"></i>
              </div>
              <div className="absolute -inset-1 rounded-lg bg-gradient-to-br from-red-500/10 to-pink-500/10 animate-ping opacity-50"></div>
            </div>
            <div>
              All Stashes ({overviewData.stashes.length})
              <div className="h-0.5 w-0 bg-gradient-to-r from-red-400 to-pink-400 animate-[width-expand_1.5s_ease-out_0.3s_forwards] rounded-full"></div>
            </div>
          </h3>
        </div>
        
        <div className="flex-1 overflow-y-auto custom-scrollbar relative min-h-0">
          {overviewData.stashes.length === 0 ? (
            <div className="glass-card rounded-xl p-8 text-center animate-fadeIn">
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-red-500/10 to-pink-500/10 animate-ping" style={{ animationDuration: '3s' }}></div>
                <div className="absolute inset-4 rounded-full bg-gradient-to-r from-red-500/5 to-pink-500/5 animate-ping" style={{ animationDuration: '2s', animationDelay: '0.5s' }}></div>
                
                <div className="relative w-20 h-20 mx-auto rounded-xl bg-gradient-to-br from-red-600/20 to-pink-600/20 flex items-center justify-center animate-float">
                  <i className="fas fa-box-open text-4xl text-red-400 animate-pulse"></i>
                </div>
              </div>
              <h4 className="text-xl font-bold gradient-text mb-3 animate-slideInLeft">No Stashes Found</h4>
              <p className="text-gray-300 animate-fadeIn" style={{ animationDelay: '0.2s' }}>No stash data is currently available</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {overviewData.stashes.map((stash, index) => (
                <div 
                  key={index} 
                  className="group relative overflow-hidden glass-card rounded-xl p-4 hover:bg-white/10 transition-all duration-700 hover:scale-105 hover:-translate-y-1 border border-white/10 hover:border-red-400/50 animate-fadeIn hover:shadow-xl hover:shadow-red-500/25"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-red-600/10 via-pink-600/5 to-red-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center flex-grow min-w-0">
                        <div className="relative mr-3 flex-shrink-0">
                          <div className="w-10 h-10 bg-gradient-to-br from-red-500/20 to-pink-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                            <i className="fas fa-box text-red-400"></i>
                          </div>
                          <div className="absolute -inset-1 bg-gradient-to-br from-red-500/10 to-pink-500/10 rounded-lg opacity-0 group-hover:opacity-100 animate-ping transition-opacity duration-300"></div>
                        </div>
                        <div className="min-w-0 flex-grow">
                          <h4 className="text-white font-bold group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-red-400 group-hover:to-pink-400 group-hover:bg-clip-text transition-all duration-500 mb-1 truncate">
                            {stash.name || 'Unknown Stash'}
                          </h4>
                          <p className="text-xs text-gray-400 bg-gray-800/50 px-2 py-1 rounded-md font-medium truncate">{stash.owner || 'Unknown Owner'}</p>
                        </div>
                      </div>
                      <button 
                        className="relative overflow-hidden bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500 text-white font-bold px-3 py-1.5 rounded-lg text-xs opacity-0 group-hover:opacity-100 transition-all duration-500 hover:scale-110 hover:-translate-y-1 shadow-lg flex-shrink-0 ml-2"
                        onClick={() => handleOpenModal('stash', stash)}
                      >
                        <div className="relative flex items-center space-x-1">
                          <i className="fas fa-info-circle"></i>
                          <span>Details</span>
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-r from-red-400/20 to-pink-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                      </button>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center p-2 bg-white/5 rounded-lg group-hover:bg-white/10 transition-all duration-300">
                        <span className="text-gray-400 font-medium flex items-center">
                          <i className="fas fa-archive mr-2 text-red-400 text-xs"></i>
                          Items:
                        </span>
                        <span className="text-white font-bold">{formatNumber(stash.items ? Object.keys(stash.items).length : 0)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )}

      {isPlayerModalOpen && selectedPlayer && (
        <PlayerDetailsModal player={selectedPlayer} onClose={() => handleCloseModal('player')} />
      )}
      {isVehicleModalOpen && selectedVehicle && (
        <VehicleDetailsModal vehicle={selectedVehicle} onClose={() => handleCloseModal('vehicle')} />
      )}
      {isStashModalOpen && selectedStash && (
        <StashDetailsModal stash={selectedStash} onClose={() => handleCloseModal('stash')} />
      )}
    </div>
  );
}

export default Overview;